import 'phoenix_html'

// Establish Phoenix Socket and LiveView configuration.
import { Socket, LongPoll } from 'phoenix'
import { LiveSocket } from 'phoenix_live_view'
import topbar from 'topbar'
import { initSegment } from './analytics'
import { convertMapKeysFromSnakeToCamelCase } from './hooks/analytics'
import { Hooks } from './hooks'
import { datadogRum } from '@datadog/browser-rum'
import { calculateZoom } from './utils/zoom_metadata'

const csrfToken = document.querySelector("meta[name='csrf-token']").getAttribute('content')

/**
 Below we grab the query params this is to provide a way to override the
 socket transport mechanism. This only exists to allow us to test long
 polling is working correctly.

 Notes:

 1. Setting the `transport` param to `longpoll` does not allow us to test the
 "fallback" functionality if websockets fail. The only way to do that is to
 cause the websocket connection itself to fail.

 2. If you need to test the fallback functionality, the "easiest" way is to set
 the LiveSocket path to one that does not exist:

    const liveSocket = new LiveSocket('/tacos-are-delicious', Socket, {
      // ...
    })

 Then in the `onConnError` override further down in this file set the socket's
 endpoint property to the correct path:

    socket.onConnError(() => {
      // ...
      socket.endpoint = '/live'`
      socket.connect()
    })

 This will cause the initial websocket connection to fail, and allow the
 long-polling mechanism to successfully connect.
 */

const urlParams = new URLSearchParams(document.location.search)
const liveSocketTransport = urlParams.get('transport') === 'longpoll' ? LongPoll : WebSocket

// Create the LiveSocket
const liveSocket = new LiveSocket('/live', Socket, {
  params: { _csrf_token: csrfToken },
  hooks: Hooks,
  longpollerTimeout: 45000, // milliseconds
  transport: liveSocketTransport,
})

// Setup for long-polling fallback
const socket = liveSocket.socket
const originalOnConnError = socket.onConnError
let fallbackToLongPoll = true

socket.onOpen(() => {
  fallbackToLongPoll = false
  // Capture which transport method is used and report it to datadog.
  datadogRum.addAction('socket.connected', {
    member_id: window.MEMBER_ID,
    transport: socket.transport === LongPoll ? 'LongPoll' : 'Websocket',
  })
})

socket.onConnError = (...args) => {
  if (fallbackToLongPoll) {
    // No longer fallback to long polling
    fallbackToLongPoll = false
    // Close the socket with an error code
    socket.disconnect(null, 3000)
    // Set transport to `LongPoll`
    socket.transport = LongPoll
    // Attempt to reconnect using `LongPoll`
    socket.connect()
  } else {
    originalOnConnError.apply(socket, args)
  }
}

// Analytics and Metrics Reporting

initSegment()

// Enrollment completes with a full-page redirect; pushed LiveView events must use
// `dispatch: :before` server-side or they are dropped. Links anonymous Segment activity to Papa id.
window.addEventListener('phx:segment_identify_enrollment', e => {
  const detail = e.detail || {}
  const userId = detail.user_id
  const traitsRaw = detail.traits || {}

  if (!window.analytics?.identify || !userId) {
    return
  }

  window.analytics.identify(userId, convertMapKeysFromSnakeToCamelCase(traitsRaw))
})

// We don't want to capture data on pages without a logged-in user
if (window.DATA_DOG_RUM_ENABLED === 'true' && !!window.MEMBER_ID) {
  datadogRum.init({
    applicationId: '1df4df16-4b10-43e3-b57d-87ab1f0a67df',
    clientToken: 'pub80fe27a69ddcb10ae5c01a07b1067bf4',
    site: 'datadoghq.com',
    service: 'member_web',
    env: window.ENV,
    // Specify a version number to identify the deployed version of your application in Datadog
    // version: '1.0.0',
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    trackUserInteractions: false,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
  })

  datadogRum.setUser({ id: window.MEMBER_ID })
  datadogRum.setGlobalContextProperty('zoom_metadata', calculateZoom())
  datadogRum.startSessionReplayRecording()
}

// Show progress bar on live navigation and form submits
topbar.config({ barColors: { 0: '#29d' }, shadowColor: 'rgba(0, 0, 0, .3)' })
window.addEventListener('phx:page-loading-start', info => topbar.show())
window.addEventListener('phx:page-loading-stop', info => {
  topbar.hide()

  if (info.detail.kind === 'element' && info.detail.target.dataset.papaScrollTop) {
    window.scrollTo(0, 0)
  }
})

// Connect if there are any LiveViews on the page, this needs to happen
// after the metric and analytic reporting is initialized, or we may miss
// some data.
liveSocket.connect()

// expose liveSocket on window for web console debug logs and latency simulation:
// >> liveSocket.enableDebug()
// >> liveSocket.enableLatencySim(1000)  // enabled for duration of browser session
// >> liveSocket.disableLatencySim()
window.liveSocket = liveSocket

// This *would* reload the page on changes to assets, but doesn't work at the
// moment, and causes an error in the browser console when deployed.
//
// new EventSource('/esbuild').addEventListener('change', e => {
//   const { added, removed, updated } = JSON.parse(e.data)
//
//   if (!added.length && !removed.length && updated.length === 1) {
//     for (const link of document.getElementsByTagName("link")) {
//       const url = new URL(link.href)
//
//       if (url.host === location.host && url.pathname === updated[0]) {
//         const next = link.cloneNode()
//         next.href = updated[0] + '?' + Math.random().toString(36).slice(2)
//         next.onload = () => link.remove()
//         link.parentNode.insertBefore(next, link.nextSibling)
//         return
//       }
//     }
//   }
//
//   location.reload()
// })
